import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { BarChart3, List } from 'lucide-react';
import { usePlanMatrizDashboard } from '@/api/plan-matriz';
import { useAmbitoStore } from '@/stores/ambito.store';
import { useAmbitos, AMBITOS_FALLBACK } from '@/hooks/use-ambitos';
import { NeuCard } from '@/components/ui/neu-card';
import { NeuButton } from '@/components/ui/neu-button';
import { NeuSelect } from '@/components/ui/neu-select';
import { MonthDashboardCard } from '@/components/domain/month-dashboard-card';
import { getCurrentPeriod } from '@/lib/periodo';

const _period = getCurrentPeriod().split('-').map(Number);
const CURRENT_YEAR = _period[0] ?? new Date().getFullYear();
const CURRENT_MONTH = _period[1] ?? (new Date().getMonth() + 1);
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 2022 }, (_, i) => CURRENT_YEAR - i);

export function PlanMatrizDashboardPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState(CURRENT_YEAR);

  const { selectedAmbitoId, setSelectedAmbito } = useAmbitoStore();
  const { data: ambitos } = useAmbitos();
  const ambitoList = ambitos ?? AMBITOS_FALLBACK;

  // Default to first ambito if none selected
  const effectiveAmbitoId = selectedAmbitoId ?? ambitoList[0]?.id;

  const { data, isLoading, isError } = usePlanMatrizDashboard(year, effectiveAmbitoId);
  const months = useMemo(() => data?.data ?? [], [data?.data]);

  // Split current month (hero) from rest for current year
  const { heroMonth, gridMonths } = useMemo(() => {
    if (year !== CURRENT_YEAR || months.length === 0) {
      return { heroMonth: null, gridMonths: months };
    }
    const hero = months.find((m) => m.month === CURRENT_MONTH) ?? null;
    const grid = months.filter((m) => m.month !== CURRENT_MONTH).sort((a, b) => b.month - a.month);
    return { heroMonth: hero, gridMonths: grid };
  }, [year, months]);

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6 md:mb-8">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-7 w-7 text-sime-accent" />
          <h1 className="text-2xl md:text-[34px] font-bold text-sime-text-primary tracking-tight-lg">
            Plan Matriz
          </h1>
        </div>
        <NeuButton
          variant="ghost"
          size="sm"
          onClick={() => { void navigate('/plan-matriz/lista'); }}
        >
          <List className="h-4 w-4 mr-2" />
          Ver listado
        </NeuButton>
      </div>

      {/* Filters */}
      <NeuCard className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          {/* Year selector */}
          <NeuSelect
            options={YEAR_OPTIONS.map((y) => ({ value: String(y), label: String(y) }))}
            value={String(year)}
            onChange={(v) => { setYear(Number(v)); }}
            aria-label="Seleccionar ano"
            className="min-w-[130px]"
          />

          {/* Ambito selector */}
          <NeuSelect
            options={[
              { value: '', label: 'Todos los ambitos' },
              ...ambitoList.map((a) => ({ value: String(a.id), label: a.nombre })),
            ]}
            value={String(effectiveAmbitoId ?? '')}
            onChange={(v) => { setSelectedAmbito(v ? Number(v) : null); }}
            aria-label="Seleccionar ambito"
            className="min-w-[180px]"
          />
        </div>
      </NeuCard>

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <NeuCard key={i} className="animate-pulse">
              <div className="h-5 bg-sime-surface-raised rounded w-1/3 mb-4" />
              <div className="h-24 bg-sime-surface-raised rounded-full w-24 mx-auto mb-4" />
              <div className="h-8 bg-sime-surface-raised rounded w-1/2 mx-auto mb-4" />
              <div className="h-16 bg-sime-surface-raised rounded" />
            </NeuCard>
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <NeuCard className="text-center py-12">
          <BarChart3 className="h-12 w-12 mx-auto mb-3 text-sime-text-tertiary" />
          <p className="text-sime-text-secondary font-medium">
            Error al cargar el dashboard
          </p>
          <p className="text-sime-text-tertiary text-sm mt-1">
            Intente nuevamente o verifique su conexion.
          </p>
        </NeuCard>
      )}

      {/* Data */}
      {!isLoading && !isError && (
        <>
          {/* Hero card for current month */}
          {heroMonth && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="mb-6"
            >
              <MonthDashboardCard data={heroMonth} hero index={0} />
            </motion.div>
          )}

          {/* Grid of month cards */}
          {gridMonths.length > 0 ? (
            <>
              {heroMonth && (
                <h2 className="text-lg font-semibold text-sime-text-primary mb-4">
                  Meses anteriores
                </h2>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {gridMonths.map((monthData, i) => (
                  <MonthDashboardCard
                    key={monthData.month}
                    data={monthData}
                    index={i + (heroMonth ? 1 : 0)}
                  />
                ))}
              </div>
            </>
          ) : months.length === 0 ? (
            <NeuCard className="text-center py-12">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 text-sime-text-tertiary" />
              <p className="text-sime-text-secondary font-medium">
                No hay datos para {year}
              </p>
              <p className="text-sime-text-tertiary text-sm mt-1">
                Seleccione otro ano o verifique que existen tickets en el periodo.
              </p>
            </NeuCard>
          ) : null}
        </>
      )}
    </div>
  );
}
